import axios from "axios";
import { getAuth } from "firebase/auth";
import { z } from "zod";

// Create axios instance with base URL and default headers
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Schemas
export const CreatePostSchema = z.object({
  title: z.string().min(1, "Title is required"),
  body: z.string().min(1, "Content is required"),
  tags: z.array(z.string()),
});

export const UpdatePostSchema = z.object({
  title: z.string().min(1, "Title is required"),
  body: z.string().min(1, "Content is required"),
  tags: z.array(z.string()),
});

export const CreateCommentSchema = z.object({
  body: z.string().min(1, "Comment is required"),
});

// Types
export interface Post {
  _id: string;
  title: string;
  body: string;
  authorId: string;
  authorEmail: string;
  tags: string[];
  comments: Comment[];
  createdAt: string;
  updatedAt: string;
  isAuthor?: boolean;
}

export interface Comment {
  _id: string;
  body: string;
  authorId: string;
  authorEmail: string;
  createdAt: string;
}

// API Functions
export async function getPosts(page = 1, limit = 10): Promise<Post[]> {
  const response = await api.get(`/posts?page=${page}&limit=${limit}`);
  return response.data;
}

export async function getLatestPosts(limit = 5): Promise<Post[]> {
  const response = await api.get(`/posts/latest?limit=${limit}`);
  return response.data;
}

export async function getPost(id: string): Promise<Post> {
  const response = await api.get(`/posts/${id}`);
  return response.data;
}

export async function createPost(
  data: z.infer<typeof CreatePostSchema>,
): Promise<Post> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Must be logged in to create a post");

  const response = await api.post("/posts", {
    ...data,
    authorId: user.uid,
    authorEmail: user.email,
  });
  return response.data;
}

export async function updatePost(
  id: string,
  data: z.infer<typeof UpdatePostSchema>,
): Promise<Post> {
  const response = await api.put(`/posts/${id}`, data);
  return response.data;
}

export async function deletePost(id: string): Promise<void> {
  await api.delete(`/posts/${id}`);
}

export async function addComment(
  postId: string,
  data: z.infer<typeof CreateCommentSchema>,
): Promise<Comment> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Must be logged in to comment");

  const response = await api.post(`/posts/${postId}/comments`, {
    ...data,
    authorId: user.uid,
    authorEmail: user.email,
  });
  return response.data;
}

export { api };
